import React, {useMemo, useState, useEffect, useRef, useDeferredValue} from "react";
import {createPortal} from "react-dom";
import {useQuery} from "@tanstack/react-query";
import {Printer, X, Tag, RotateCcw, Search, Plus, Minus, Trash2, AlertCircle, Package, Loader, ChevronDown, ChevronUp} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {getFormat, getTypeConfig, isValidFormat, LABEL_FORMATS, LABEL_LIMITS} from "./labelConfig";
import useLabelTemplate from "./useLabelTemplate";
import LabelPreview from "./LabelPreview";
import LabelContent from "./LabelContent";
import {printLabels, PRINT_ROOT_ID} from "./printLabels";
import AppleSelect from "../Common/AppleSelect";

const {minPerItem, maxPerItem, maxTotal} = LABEL_LIMITS;

const clampQty = (n) => Math.min(Math.max(n, minPerItem), maxPerItem);

/**
 * Modale d'impression d'étiquettes.
 *
 * Permet de composer un lot : plusieurs produits, chacun avec une quantité éditable,
 * un format et des informations à afficher, puis de lancer l'impression de toutes les
 * étiquettes en une fois (modèle thermique : 1 étiquette = 1 page).
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {string} type     - "product" | "delivery" | "client"
 * @param {object[]} items  - éléments pré-sélectionnés (depuis la liste). Pour le type
 *                            "product", d'autres produits peuvent être ajoutés via la recherche.
 */
const LabelPrintModal = ({open, onClose, type = "product", items = []}) => {
    const config = getTypeConfig(type);
    const {format: formatId, selectedFields, setFormat, toggleField, reset} = useLabelTemplate(type);
    const format = getFormat(formatId);

    const titleField = useMemo(() => config.fields.find((f) => f.kind === "title"), [config]);
    const rowLabel = (it) => (titleField ? titleField.accessor(it) : null) || it?.name || it?.id || "—";
    const rowSub = (it) => it?.barcode || it?.reference || "";

    // Lignes du lot : { item, qty } — qty est saisie sous forme de chaîne pour permettre
    // une édition fluide (champ vide transitoire), normalisée au blur / à l'impression.
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [showProducts, setShowProducts] = useState(true);
    const prevOpen = useRef(false);

    // (Re)initialise les lignes à l'ouverture de la modale, à partir des items pré-sélectionnés.
    useEffect(() => {
        if (open && !prevOpen.current) {
            const seen = new Set();
            const seeded = [];
            items.forEach((it) => {
                const id = it?.id ?? JSON.stringify(it);
                if (seen.has(id)) return;
                seen.add(id);
                seeded.push({item: it, qty: "1"});
            });
            setRows(seeded);
            setError("");
            setProductQuery("");
        }
        prevOpen.current = open;
    }, [open, items]);

    // ── Recherche produit (ajout au lot) ─────────────────────────────
    const [productQuery, setProductQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(productQuery.trim()), 350);
        return () => clearTimeout(t);
    }, [productQuery]);

    const searchEnabled = open && type === "product" && debouncedQuery.length >= 2;
    // Une requête qui ne contient que des chiffres est traitée comme un code-barres.
    const isBarcodeLike = /^\d{2,}$/.test(debouncedQuery);

    // Recherche par nom (filtrée côté API).
    const {data: nameResults = [], isFetching: searchingName} = useQuery({
        queryKey: ["label-product-search-name", debouncedQuery],
        queryFn: async () => {
            const acc = await secureStorage.getItem("selectedAccount");
            const res = await api.get(
                `/products?page=1&itemsPerPage=10&name=${encodeURIComponent(debouncedQuery)}`,
                {headers: {"X-account-id": acc.id}}
            );
            return res.data?.data || res.data?.items || res.data || [];
        },
        enabled: searchEnabled,
        staleTime: 60000,
        keepPreviousData: true,
    });

    // Recherche par code-barres : l'API n'expose pas de filtre `barcode`, on récupère donc
    // un lot large (mis en cache) puis on filtre côté client — produit ET variantes —
    // comme dans ProductList. La requête n'est lancée que pour une saisie numérique.
    const {data: allProducts = [], isFetching: searchingBarcode} = useQuery({
        queryKey: ["label-product-all"],
        queryFn: async () => {
            const acc = await secureStorage.getItem("selectedAccount");
            const res = await api.get(`/products?page=1&itemsPerPage=1000`, {headers: {"X-account-id": acc.id}});
            return res.data?.data || res.data?.items || res.data || [];
        },
        enabled: searchEnabled && isBarcodeLike,
        staleTime: 300000,
        keepPreviousData: true,
    });

    const barcodeResults = useMemo(() => {
        if (!isBarcodeLike) return [];
        const term = debouncedQuery.toLowerCase();
        return allProducts
            .filter(
                (p) =>
                    p.barcode?.toLowerCase().includes(term) ||
                    p.variants?.some((v) => v.barcode?.toLowerCase().includes(term))
            )
            .slice(0, 10);
    }, [allProducts, debouncedQuery, isBarcodeLike]);

    // Fusion nom + code-barres, dédupliquée par id.
    const searchResults = useMemo(() => {
        const map = new Map();
        [...nameResults, ...barcodeResults].forEach((p) => {
            if (p && p.id != null && !map.has(p.id)) map.set(p.id, p);
        });
        return Array.from(map.values()).slice(0, 12);
    }, [nameResults, barcodeResults]);

    const searching = searchingName || searchingBarcode;

    // ── Mutations sur les lignes ─────────────────────────────────────
    const addRow = (item) => {
        setRows((prev) => {
            const i = prev.findIndex((r) => r.item.id === item.id);
            if (i >= 0) {
                const next = [...prev];
                const q = clampQty((parseInt(next[i].qty, 10) || 0) + 1);
                next[i] = {...next[i], qty: String(q)};
                return next;
            }
            return [...prev, {item, qty: "1"}];
        });
    };
    const setQty = (idx, raw) => {
        const cleaned = raw.replace(/[^\d]/g, "");
        setRows((prev) => prev.map((r, i) => (i === idx ? {...r, qty: cleaned} : r)));
    };
    const normalizeQty = (idx) => {
        setRows((prev) =>
            prev.map((r, i) => (i === idx ? {...r, qty: String(clampQty(parseInt(r.qty, 10) || minPerItem))} : r))
        );
    };
    const stepQty = (idx, delta) => {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== idx) return r;
                return {...r, qty: String(clampQty((parseInt(r.qty, 10) || 0) + delta))};
            })
        );
    };
    const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
    const applyToAll = (raw) => {
        const v = clampQty(parseInt(raw, 10) || minPerItem);
        setRows((prev) => prev.map((r) => ({...r, qty: String(v)})));
    };

    // ── Calcul du lot final ──────────────────────────────────────────
    const totalRequested = useMemo(
        () => rows.reduce((s, r) => s + (parseInt(r.qty, 10) || 0), 0),
        [rows]
    );

    const printList = useMemo(() => {
        const list = [];
        for (const r of rows) {
            const n = Math.min(parseInt(r.qty, 10) || 0, maxPerItem);
            for (let i = 0; i < n && list.length < maxTotal; i++) list.push(r.item);
            if (list.length >= maxTotal) break;
        }
        return list;
    }, [rows]);

    // Le rendu du conteneur d'impression est différé : la saisie de quantité reste fluide
    // même quand le lot contient des centaines d'étiquettes.
    const deferredPrintList = useDeferredValue(printList);

    // Efface le message d'erreur dès que l'utilisateur corrige la configuration.
    useEffect(() => {
        if (error) setError("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, selectedFields, formatId]);

    if (!open) return null;

    const previewItem = rows[0]?.item || items[0];
    const overCapacity = totalRequested > maxTotal;

    const handlePrint = () => {
        if (rows.length === 0) return setError("Ajoutez au moins un produit à imprimer.");
        if (selectedFields.length === 0) return setError("Sélectionnez au moins une information à afficher.");
        if (!isValidFormat(formatId)) return setError("Format d'étiquette invalide.");
        if (rows.some((r) => (parseInt(r.qty, 10) || 0) < minPerItem)) {
            return setError(`La quantité minimale est de ${minPerItem} par produit.`);
        }
        if (printList.length === 0) return setError("Aucune étiquette à imprimer.");
        setError("");
        try {
            printLabels(format);
        } catch (e) {
            setError("Erreur lors de l'impression : " + (e?.message || "imprimante indisponible"));
        }
    };

    const canPrint = rows.length > 0 && selectedFields.length > 0 && printList.length > 0;

    return (
        <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                <div
                    className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
                    style={{boxShadow: "0 25px 60px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.05)"}}
                >
                    {/* Header */}
                    <div className="px-5 py-4 flex justify-between items-center border-b border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Tag className="w-4 h-4 text-primary"/>
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{config.title}</h2>
                                <p className="text-xs text-gray-400">
                                    {rows.length} produit{rows.length > 1 ? "s" : ""} · {printList.length} étiquette{printList.length > 1 ? "s" : ""}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <X className="w-4 h-4"/>
                        </button>
                    </div>

                    {/* Body */}
                    <div className="flex flex-col gap-6 p-5 overflow-y-auto">
                        {/* Produits à imprimer */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <button
                                    type="button"
                                    onClick={() => setShowProducts(prev => !prev)}
                                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary transition-colors"
                                >
                                    {showProducts ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                                    Produits à imprimer
                                    <span className="text-xs text-gray-400 font-normal ml-1">({rows.length})</span>
                                </button>
                                {showProducts && rows.length > 1 && (
                                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                        <span>Tout à</span>
                                        <input
                                            type="number"
                                            min={minPerItem}
                                            max={maxPerItem}
                                            defaultValue={1}
                                            onChange={(e) => applyToAll(e.target.value)}
                                            className="w-16 px-2 py-1 text-xs text-center border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                            title="Appliquer cette quantité à tous les produits"
                                        />
                                    </div>
                                )}
                            </div>

                            {showProducts && (<>
                            {/* Recherche d'ajout (produits uniquement) */}
                            {type === "product" && (
                                <div className="relative mb-2.5">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom ou code-barres…"
                                        value={productQuery}
                                        onChange={(e) => setProductQuery(e.target.value)}
                                        className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    />
                                    {searching && (
                                        <Loader className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"/>
                                    )}
                                    {searchEnabled && (searchResults.length > 0 || !searching) && (
                                        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                            {searchResults.length === 0 ? (
                                                <div className="px-3 py-2.5 text-sm text-gray-400">Aucun produit trouvé.</div>
                                            ) : (
                                                searchResults.map((p) => {
                                                    const already = rows.some((r) => r.item.id === p.id);
                                                    return (
                                                        <button
                                                            key={p.id}
                                                            type="button"
                                                            onClick={() => addRow(p)}
                                                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                                                        >
                                                            <span className="min-w-0">
                                                                <span className="block text-sm text-gray-800 dark:text-gray-200 truncate">{rowLabel(p)}</span>
                                                                {rowSub(p) && <span className="block text-xs text-gray-400 font-mono truncate">{rowSub(p)}</span>}
                                                            </span>
                                                            <span className={`flex items-center gap-1 text-xs font-medium ${already ? "text-gray-400" : "text-primary"}`}>
                                                                <Plus className="w-3.5 h-3.5"/> {already ? "Ajouté" : "Ajouter"}
                                                            </span>
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Liste des lignes */}
                            {rows.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 px-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl text-center">
                                    <Package className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2"/>
                                    <p className="text-sm text-gray-400">
                                        {type === "product" ? "Recherchez un produit ci-dessus pour l'ajouter au lot." : "Aucun élément à imprimer."}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                                    {rows.map((r, idx) => (
                                        <div
                                            key={r.item.id ?? idx}
                                            className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{rowLabel(r.item)}</div>
                                                {rowSub(r.item) && (
                                                    <div className="text-xs text-gray-400 font-mono truncate">{rowSub(r.item)}</div>
                                                )}
                                            </div>
                                            {/* Stepper quantité */}
                                            <div className="flex items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => stepQty(idx, -1)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-l-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                    aria-label="Diminuer"
                                                >
                                                    <Minus className="w-3.5 h-3.5"/>
                                                </button>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={r.qty}
                                                    onChange={(e) => setQty(idx, e.target.value)}
                                                    onBlur={() => normalizeQty(idx)}
                                                    className="w-12 h-7 px-1 text-sm text-center border-y border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary"
                                                    aria-label="Quantité"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => stepQty(idx, 1)}
                                                    className="w-7 h-7 flex items-center justify-center rounded-r-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                    aria-label="Augmenter"
                                                >
                                                    <Plus className="w-3.5 h-3.5"/>
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeRow(idx)}
                                                className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                aria-label="Retirer"
                                            >
                                                <Trash2 className="w-4 h-4"/>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {overCapacity && (
                                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                    Le total demandé ({totalRequested}) dépasse la limite de {maxTotal} étiquettes par impression.
                                    Seules les {maxTotal} premières seront imprimées.
                                </p>
                            )}
                            </>)}
                        </div>

                        {/* Configuration + aperçu */}
                        <div className="flex flex-col md:flex-row gap-6 border-t border-gray-100 dark:border-gray-800 pt-5">
                            {/* Colonne configuration */}
                            <div className="md:w-1/2 space-y-5">
                                {/* Format */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                                        Format de l'étiquette
                                    </label>
                                    <AppleSelect
                                        value={formatId}
                                        onChange={(e) => setFormat(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    >
                                        {LABEL_FORMATS.map((f) => (
                                            <option key={f.id} value={f.id}>{f.label}</option>
                                        ))}
                                    </AppleSelect>
                                </div>

                                {/* Champs à afficher */}
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Informations à afficher
                                        </label>
                                        <button
                                            type="button"
                                            onClick={reset}
                                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
                                        >
                                            <RotateCcw size={13}/> Réinitialiser
                                        </button>
                                    </div>
                                    <div className="space-y-1.5">
                                        {config.fields.map((field) => (
                                            <label
                                                key={field.id}
                                                className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary/50 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFields.includes(field.id)}
                                                    onChange={() => toggleField(field.id)}
                                                    className="w-4 h-4 accent-primary"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{field.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Colonne aperçu */}
                            <div className="md:w-1/2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                                    Aperçu
                                </label>
                                <div className="flex items-center justify-center min-h-[220px] bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                                    {!previewItem ? (
                                        <p className="text-sm text-gray-400">Ajoutez un produit pour l'aperçu.</p>
                                    ) : selectedFields.length === 0 ? (
                                        <p className="text-sm text-gray-400">Sélectionnez au moins une information.</p>
                                    ) : (
                                        <LabelPreview
                                            type={type}
                                            item={previewItem}
                                            format={format}
                                            selectedFields={selectedFields}
                                        />
                                    )}
                                </div>
                                <p className="text-xs text-gray-400 mt-2 text-center">
                                    Format {format.label} · {printList.length} étiquette{printList.length > 1 ? "s" : ""} à imprimer
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            {error && (
                                <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0"/> {error}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handlePrint}
                                disabled={!canPrint}
                                title="Ouvre la boîte de dialogue d'impression (choisir l'imprimante installée sur le poste)"
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                <Printer size={16}/> Imprimer{printList.length > 0 ? ` (${printList.length})` : ""}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conteneur d'impression (masqué à l'écran, visible uniquement à l'impression) */}
            {createPortal(
                <div id={PRINT_ROOT_ID} aria-hidden="true" style={{position: "absolute", left: "-9999px", top: 0}}>
                    {deferredPrintList.map((item, i) => (
                        <div className="label-print-page" key={i}>
                            <LabelContent type={type} item={item} format={format} selectedFields={selectedFields}/>
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

export default LabelPrintModal;
